import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnInit,
    Output,
    Renderer2,
} from "@angular/core";
import { S25EditableAbstract } from "../s25-editable/s25.editable.abstract";
import { S25ItemI } from "../../pojo/S25ItemI";
import { SearchCriteriaType } from "../../pojo/SearchCriteriaI";
import { OrganizationService } from "../../services/organization.service";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { SpaceService } from "../../services/space.service";
import { ResourceService } from "../../services/resource.service";
import { MultiselectModelI } from "../s25-multiselect/s25.multiselect.component";
import { Bind } from "../../decorators/bind.decorator";
import { S25LoadingApi } from "../s25-loading/loading.api";
import { ResourceMicroService } from "../../services/resource.micro.service";
import { Item } from "../../pojo/Item";
import { EventMicroService } from "../../services/event.micro.service";
import { Debounce } from "../../decorators/debounce.decorator";

export interface EditablePropertyModelI extends MultiselectModelI {
    canEdit?: boolean;
    // ID of the specific object we are interacting with
    itemId: number;
    // ID of obj type; org/space/resource
    itemTypeId: Item.Ids;
    // array of properties fed into multiselect
    properties?: S25ItemI[];
    // Property type used for search criteria in multiselect
    propType: SearchCriteriaType["type"];
    // Used to dynamically build payload
    propPayloadType: "category" | "feature" | "requirement";
    noneText: string;
}

@TypeManagerDecorator("s25-ng-editable-property")
@Component({
    selector: "s25-ng-editable-property",
    template: `
        @if (init) {
            <s25-loading-inline [model]="{}"></s25-loading-inline>
            <ng-content></ng-content>
            @for (property of model.properties; track property) {
                <div class="property-wrapper">
                    @if (model.canEdit) {
                        <s25-ng-icon-button
                            [type]="'close'"
                            (click)="model.removeAction(property, true)"
                        ></s25-ng-icon-button>
                    }
                    <span class="ng-binding">{{ property.itemName }}</span>
                    @if (property.quantity) {
                        <span class="num-wrapper">
                            <s25-ng-editable-number
                                [val]="property.quantity"
                                [hasCommit]="true"
                                [readOnly]="!model.canEdit"
                                [onCommit]="saveQuantity"
                                [min]="1"
                                [max]="99999999"
                                (valChange)="objectToUpdate = { itemId: +property.itemId, quantity: $event }"
                            ></s25-ng-editable-number>
                        </span>
                    }
                </div>
            }
            @if (!model.canEdit && !properties.length) {
                {{ model.noneText }}
            }
            @if (model.canEdit) {
                <s25-ng-multiselect-search-criteria
                    [type]="model.propType"
                    [popoverOnBody]="true"
                    [modelBean]="model"
                    [honorMatching]="true"
                    [useSecurity]="true"
                    [selectedItems]="properties"
                ></s25-ng-multiselect-search-criteria>
            }
        }
    `,
    host: {
        "[class.features]": "model.propPayloadType === 'feature'",
    },
    styles: `
        :host {
            s25-loading-inline {
                flex-basis: 100%;
            }

            .property-wrapper {
                display: flex;
                gap: 5px;

                .num-wrapper {
                    margin-left: auto;
                }

                & > span {
                    position: relative;
                    top: 2px;
                }
            }

            &:not(.features) {
                display: flex;
                flex-wrap: wrap;
                gap: 1.5em;

                s25-ng-multiselect-search-criteria {
                    flex-basis: 100%;
                }
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25EditablePropertyComponent extends S25EditableAbstract<any> implements OnInit {
    @Input() model: EditablePropertyModelI;
    @Input() useOnDone?: boolean; // commit all selections at once rather than each individually
    @Output() changed: EventEmitter<any> = new EventEmitter();

    getType = () => this.model.propPayloadType;

    propNameKey: string;
    propIdKey: string;
    microPropIdKey: string;
    microPropWrapperKey: string;
    objectToUpdate: { itemId: number; quantity: number };

    // service to be called
    objectService: (itemId: number, payload: any) => Promise<any>;
    microService: (payload: any, ...args: any[]) => Promise<any> = undefined;

    init: boolean = false;
    properties: S25ItemI[] = [];

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
        private zone: NgZone,
        private renderer: Renderer2,
    ) {
        super(elementRef, cd, zone, renderer);
        this.elementRef.nativeElement.angBridge = this; //bridge to AngularJS; used for AngJS to set model values and call setter fns
    }

    ngOnInit() {
        super.ngOnInit();

        // Switch service based on object type
        switch (this.model.itemTypeId) {
            case Item.Ids.Event:
                this.microService = EventMicroService.microPutEventDetail;
                break;
            case Item.Ids.Organization:
                this.objectService = OrganizationService.putOrganization;
                break;
            case Item.Ids.Location:
                this.microService = SpaceService.microUpdate;
                this.objectService = SpaceService.putSpace;
                break;
            case Item.Ids.Resource:
                this.microService = ResourceMicroService.saveResource;
                this.objectService = ResourceService.putResource;
                break;
        }

        // Switch payload keys based on property type
        switch (this.model.propPayloadType) {
            case "category":
                this.propNameKey = "category_name";
                this.propIdKey = "category_id";
                this.microPropIdKey = "categoryId";
                this.microPropWrapperKey = "categories";
                break;
            case "feature":
                this.microPropIdKey = "featureId";
                this.microPropWrapperKey = "features";
        }

        this.properties = this.normalize(this.model.properties);

        this.model.addAction = (item: S25ItemI) => {
            if (this.useOnDone) return; //if using multiselect, addedItems set to empty before commiting selected items
            this.model.addedItems = [];
            this.editProperty(item, false).then(() => {
                return this.cd.detectChanges();
            });
        };

        this.model.removeAction = (item: S25ItemI, bypassOnDone?: boolean) => {
            if (this.useOnDone && !bypassOnDone) return; // bypass onDone when deleting individual item, otherwise wait to commit all selections
            this.model.removedItems = [];
            this.editProperty(item, true).then(() => {
                return this.cd.detectChanges();
            });
        };

        this.model.onDone = () => {
            if (this.model.addedItems.length) {
                this.editProperties(this.model.addedItems, false);
                this.model.addedItems = [];
            }
            if (this.model.removedItems.length) {
                this.editProperties(this.model.removedItems, true);
                this.model.removedItems = [];
            }
        };

        this.init = true;
        this.cd.detectChanges();
    }

    normalize(arr: S25ItemI[]): S25ItemI[] {
        return arr.map((item) => {
            item.itemId = item.itemId || item[this.propIdKey];
            item.itemName = item.itemName || item[this.propNameKey];
            return item;
        });
    }

    @Debounce(500)
    async editProperty(prop: S25ItemI, remove: boolean) {
        S25LoadingApi.init(this.elementRef.nativeElement);
        prop[this.propNameKey] = prop.itemName;
        prop[this.propIdKey] = prop.itemId;
        prop.checked = !remove;
        remove ? (prop.status = "del") : (prop.status = "new");
        // If we have defined a microService, we will use that instead.
        return (
            this.microService
                ? this.editPropertyMicro(prop)
                : this.objectService(this.model.itemId, { [this.model.propPayloadType]: prop })
        ).then(() => this.updateModelProps([prop], remove));
    }

    editProperties(props: S25ItemI[], remove: boolean) {
        S25LoadingApi.init(this.elementRef.nativeElement);
        if (this.microService) return this.updatePropsMicro(props, remove);
        let newProps: S25ItemI[] = [];

        props.map((prop) => {
            prop[this.propNameKey] = prop.itemName;
            prop[this.propIdKey] = prop.itemId;
            prop.checked = !remove;
            remove ? (prop.status = "del") : (prop.status = "new");
            return newProps.push(prop);
        });

        return this.objectService(this.model.itemId, { category: newProps }).then(() =>
            this.updateModelProps(props, remove),
        );
    }

    @Bind
    async saveQuantity() {
        const { itemId, quantity } = this.objectToUpdate ?? {};
        const payload = {
            [this.microPropWrapperKey]: [{ [this.microPropIdKey]: itemId, quantity: quantity }],
        };
        const [_, error] = await S25Util.Maybe(this.microService({ id: this.model.itemId, ...payload }));
        if (error) console.error(error);
        this.objectToUpdate = null;
    }

    async editPropertyMicro(prop: S25ItemI) {
        const isEventType = this.model.itemTypeId === Item.Ids.Event;
        const payload = {
            id: this.model.itemId,
            remove: { [this.microPropWrapperKey]: [{ [this.microPropIdKey]: prop.itemId }] },
        };
        return isEventType ? this.microService({ items: [payload] }, this.model.itemId) : this.microService(payload);
    }

    async updatePropsMicro(props: S25ItemI[], remove: boolean) {
        const accountForQuantity = !remove && this.model.propPayloadType === "feature";
        const isEventType = this.model.itemTypeId === Item.Ids.Event;
        const data = {
            [this.microPropWrapperKey]: props.map((prop) => ({
                [this.microPropIdKey]: prop.itemId,
                ...(accountForQuantity && { quantity: 1 }),
            })),
        };
        const payload = { id: this.model.itemId, ...(remove ? { remove: data } : data) };
        const [_, error] = await S25Util.Maybe(
            isEventType ? this.microService({ items: [payload] }, this.model.itemId) : this.microService(payload),
        );
        if (error) return console.error;
        if (accountForQuantity) props = props.map((prop) => ({ ...prop, quantity: 1 }));
        this.updateModelProps(props, remove);
    }

    updateModelProps(props: S25ItemI[], remove: boolean) {
        remove
            ? props.forEach((prop) => {
                  S25Util.array.inplaceRemoveByProp(this.model.properties, "itemId", prop.itemId);
                  S25Util.array.inplaceRemoveByProp(this.properties, "itemId", prop.itemId);
              })
            : props.forEach((prop) => {
                  S25Util.array.inplacePushByProp(this.model.properties, "itemId", prop.itemId, prop);
              });
        this.changed.emit(this.model.properties);
        S25LoadingApi.destroy(this.elementRef.nativeElement);
        this.cd.detectChanges();
    }
}
